import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import {
  CREATOR_SHARE_BPS,
  GRADUATION_QUOTE_ETH,
  LAUNCH_FEE_ETH,
  PLATFORM_SHARE_BPS,
  TRADE_FEE_BPS,
} from '@/lib/launchpad/constants'
import {
  DEFAULT_MAX_CONTRIBUTION_ETH,
  DEFAULT_MIN_CONTRIBUTION_ETH,
  LIQUIDITY_LOCK_MONTHS,
  PRESALE_CREATOR_MAX_PCT,
  PRESALE_FEE_BPS,
  PRESALE_LAUNCH_FEE_ETH,
  PRESALE_LP_MAX_PCT,
  PRESALE_LP_MIN_PCT,
  PRESALE_DEFAULT_TOKENS_PER_ETH,
  PROTOCOL_CHAIN,
  formatBps,
} from '@/lib/protocol'

function Terms() {
  return (
    <Layout>
      <SeoHead
        title="Terms of Use"
        description={`Penguin Pad terms for permissionless launches and presales on ${PROTOCOL_CHAIN}. No KYC. Read the risks before you trade or raise.`}
      />

      <article className="mx-auto max-w-3xl px-4 pb-20 pt-12 sm:px-6 lg:px-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Legal</p>
        <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">Terms of Use</h1>
        <p className="mt-2 text-sm text-zinc-500">
          Last updated {new Date().getFullYear()}. Settlement on {PROTOCOL_CHAIN}.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">
          These Terms govern your use of Penguin Pad, a permissionless interface for bonding-curve launches
          and community presales. By connecting a wallet, launching a project, trading, or contributing, you
          agree to them. This is not legal, tax, or investment advice.
        </p>

        <aside
          role="alert"
          className="mt-6 rounded-2xl border border-lime-400/70 bg-lime-400/10 px-4 py-4 sm:px-5"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">
            Brand warning
          </p>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-lime-300">
            Anyone who creates a project that copies Penguin Pad or its logos will be blacklisted immediately.
            Do not reuse the Penguin Pad name, wordmark, penguin artwork, or any lookalike branding on a launch or presale.
          </p>
        </aside>

        <Section title="1. The protocol">
          <p>
            Penguin Pad is a user interface for on-chain activity on
            {' '}
            <strong className="text-zinc-200">{PROTOCOL_CHAIN}</strong>
            . Quotes in the UI may be shown in USD for readability. Settlement, balances, and final prices
            are determined on-chain. Penguin Pad is not a broker, exchange, bank, or dealer, and it is not
            affiliated with Robinhood Markets, Inc. unless a separate written agreement says otherwise.
          </p>
        </Section>

        <Section title="2. Permissionless access — no KYC">
          <p>
            Anyone with a {PROTOCOL_CHAIN} wallet may launch a token, open a presale, buy, sell, or
            contribute. Penguin Pad does
            {' '}
            <strong className="text-zinc-200">not</strong>
            {' '}
            require KYC, does not run an issuer application queue, and does not geo-fence users at the
            protocol layer. You are solely responsible for whether your activity is lawful where you live.
            If your jurisdiction forbids this, do not use Penguin Pad.
          </p>
        </Section>

        <Section title="3. What you are dealing with">
          <ul>
            <li>
              Launchpad tokens are experimental digital assets priced on a public bonding curve. They may
              be treated as securities, commodities, or something else depending on your laws.
            </li>
            <li>
              Presales are community fundraising. Contributing is not a guaranteed allocation of equity,
              and it is not a bank deposit. You can lose the entire amount.
            </li>
            <li>
              Curve trading is a marketplace among wallets. Penguin Pad does not match orders off-chain
              and does not guarantee fills, uptime, or price.
            </li>
          </ul>
        </Section>

        <Section title="4. Launchpad rules (bonding curve)">
          <ul>
            <li>
              Curve launches in this interface use a constant-product market with a phantom quote reserve, via the live CurveFactory on {PROTOCOL_CHAIN}.
            </li>
            <li>
              Launch fee:
              {' '}
              {LAUNCH_FEE_ETH}
              {' '}
              ETH, paid from the connected wallet. It does not enter curve reserves.
            </li>
            <li>
              Graduation when net quote raised reaches
              {' '}
              {GRADUATION_QUOTE_ETH}
              {' '}
              ETH. After that, curve buy/sell stop and remaining curve tokens plus reserved supply seed LP.
            </li>
            <li>
              Trade fee:
              {' '}
              {formatBps(TRADE_FEE_BPS)}
              {' '}
              on the quote leg of every buy and sell, split
              {' '}
              {formatBps(CREATOR_SHARE_BPS)}
              {' '}
              creator /
              {' '}
              {formatBps(PLATFORM_SHARE_BPS)}
              {' '}
              platform. There is no extra creator tax. On the bonding curve, the creator's 75% share accrues and is disbursed at bonding (graduation). After the token lists on Uniswap v4, that same 75% share is disbursed to the creator wallet on every Uniswap swap via PenguinHook — there is no separate claim step in the normal case.
            </li>
            <li>Buys in the first ~5 seconds may also pay a decaying anti-snipe tax. Sells never do.</li>
            <li>Fee terms snapshot at create and do not change for that launch.</li>
          </ul>
        </Section>

        <Section title="5. Presale rules (frozen)">
          <ul>
            <li>Every raise publishes a soft cap and a hard cap. Hard cap must be greater than or equal to soft cap.</li>
            <li>
              Opening a presale costs
              {' '}
              {PRESALE_LAUNCH_FEE_ETH}
              {' '}
              ETH, paid by the creator wallet. This fee is independent of the raise.
            </li>
            <li>
              Caps and pledges are denominated in ETH. Each wallet contributes between the creator-set min and max. Effective max is the smaller of that max and remaining room to the hard cap. Contribute is only open during the start/end window. Withdraw stays open until close, or after a creator cancel. If unset, defaults are
              {' '}
              {DEFAULT_MIN_CONTRIBUTION_ETH}
              {' '}
              ETH min and
              {' '}
              {DEFAULT_MAX_CONTRIBUTION_ETH}
              {' '}
              ETH max.
            </li>
            <li>
              Creators enter total token supply. Tokens per ETH and listing rate are derived from supply ÷ hard cap (default rate
              {' '}
              {PRESALE_DEFAULT_TOKENS_PER_ETH.toLocaleString()}
              {' '}
              tokens per ETH at the default hard cap). They can go live now (factory still opens in 20 minutes) or schedule a later start and an end date. Creators may cancel before start, or only with more than 24 hours left; contributors can still withdraw pledged ETH. Remaining room below minContribution can only fill the hard cap as an exact leftover.
            </li>
            <li>
              <strong className="text-zinc-200">Success:</strong>
              {' '}
              raised is at least the soft cap at close, then a wallet must click finalize() to launch. Hitting the hard cap does not auto-launch, but a wallet can click finalize() as soon as the hard cap is filled. After a successful launch, contributors claim tokens at the published rate. The creator receives an ETH share of remaining proceeds after the 3% platform fee (0–30%); this factory does not expose claimTeam(). Liquidity share of remaining ETH is locked for
              {' '}
              {LIQUIDITY_LOCK_MONTHS}
              {' '}
              months at listing.
            </li>
            <li>
              <strong className="text-zinc-200">Fail:</strong>
              {' '}
              raised is below the soft cap at close. No tokens are issued. After finalize(), 100% of contributions are refundable to the contributing wallet via refund().
            </li>
            <li>Contributions that would exceed the hard cap are rejected.</li>
            <li>
              Platform fee:
              {' '}
              {formatBps(PRESALE_FEE_BPS)}
              {' '}
              of ETH raised on successful raises only. Failed raises are not charged. Remaining ETH after that fee splits to liquidity and the creator. LP must be between
              {' '}
              {PRESALE_LP_MIN_PCT}
              % and
              {' '}
              {PRESALE_LP_MAX_PCT}
              %. Creator take cannot exceed
              {' '}
              {PRESALE_CREATOR_MAX_PCT}
              % of remaining ETH.
            </li>
          </ul>
        </Section>

        <Section title="6. Listings are not endorsements">
          <p>
            Because launches and presales are permissionless, Penguin Pad does not vet teams, code, or
            tokenomics beyond what the contracts enforce. A listing is not an endorsement, audit, or
            offer to sell securities by Penguin Pad. Do your own research. You can lose everything.
          </p>
        </Section>

        <Section title="7. Your wallet, your keys">
          <p>
            You transact from a wallet you control. Penguin Pad never holds your keys and cannot reverse
            a confirmed {PROTOCOL_CHAIN} transaction. Failed transactions, wrong-network sends, and
            phishing are your risk.
          </p>
        </Section>

        <Section title="8. Prohibited use">
          <p>
            Do not use Penguin Pad to violate law, steal, launder value, exploit minors, attack the
            protocol, or impersonate Penguin Pad. Creating a project that copies Penguin Pad or its logos
            is prohibited and results in an immediate blacklist. We may hide a listing from the interface or pause
            front-end access if we reasonably believe it is required for safety. On-chain positions are
            still governed by the contracts, not by this website.
          </p>
        </Section>

        <Section title="9. No warranties; limitation of liability">
          <p>
            The interface is provided “as is.” We do not warrant uninterrupted service, accurate USD
            quotes, or that any project will succeed. To the fullest extent allowed by law, Penguin Pad
            and its contributors are not liable for lost funds, lost tokens, lost profits, or indirect
            damages arising from your use of the protocol. Smart-contract and market risk sit with you.
          </p>
        </Section>

        <Section title="10. Changes">
          <p>
            Frozen economic constants (curve formula, bond threshold, allocations, fees, success/fail
            refunds) apply to listings created under those constants. We may update these Terms and
            future listing defaults. Continued use after a change means you accept the new Terms.
          </p>
        </Section>

        <div className="mt-10 flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-400">Read the playbook, then launch or trade at your own risk.</p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/how-it-works"
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white hover:border-white/20"
            >
              How it works
            </Link>
            <Link
              to="/submit"
              className="rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-300"
            >
              Launch a project
            </Link>
          </div>
        </div>
      </article>
    </Layout>
  )
}

function Section({ title, children }: { title: string, children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-zinc-400 [&_li]:ml-4 [&_li]:list-disc [&_li]:pl-1 [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  )
}

export default Terms
